import React, { forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { createPortal } from "react-dom";
import { AlertCircle, ChevronFirst, ChevronLast, ChevronLeft, ChevronRight, Columns3, Download, ListFilter, LoaderCircle, MoreHorizontal, RotateCcw, Search } from "lucide-react";
import { useFilters, useGlobalFilter, usePagination, useRowSelect, useSortBy, useTable } from "react-table";
import ReactSelect from "react-select";

import Button from "./Button";
import EmptyState from "./EmptyState";
import IconButton from "./IconButton";
import { TableSkeleton } from "./Loading";

const defaultGetRowId = (row) => row.id;

const includesAny = (rows, ids, filterValue) => {
  const selected = filterValue.map(String);
  return rows.filter((row) => ids.some((id) => selected.includes(String(row.values[id]))));
};
includesAny.autoRemove = (value) => !Array.isArray(value) || value.length === 0;
const filterTypes = { multiselect: includesAny, select: "equals" };

const SelectionCheckbox = forwardRef(({ indeterminate, ...props }, forwardedRef) => {
  const localRef = useRef(null);
  const ref = forwardedRef || localRef;
  useEffect(() => { if (ref.current) ref.current.indeterminate = Boolean(indeterminate); }, [indeterminate, ref]);
  return <input {...props} ref={ref} type="checkbox" />;
});
SelectionCheckbox.displayName = "SelectionCheckbox";

const ResponsiveDataTable = ({
  actions = [], bulkActions = [], caption, columns, data, emptyAction,
  emptyDescription = "Try changing your search or filters.", emptyTitle = "No results found",
  error, getRowId = defaultGetRowId, initialFilters = {}, initialSort, loading = false, manual = false, onClearFilters, onExport, onPageChange, onPageSizeChange, onQueryChange, onRetry,
  pageIndex: controlledPageIndex = 0, pageCount: controlledPageCount, pagination = true, pageSize: initialPageSize = 10, pageSizeOptions = [10, 20, 50],
  searchPlaceholder = "Search records", selectable = false, showColumnSelector = true,
  showCount = false, showExport = true, showFilters = true, showSearch = true, title, totalRows,
}) => {
  const tableId = useId();
  const [activePanel, setActivePanel] = useState(null);
  const [goToPageValue, setGoToPageValue] = useState("1");
  const correctedPageRef = useRef(null);
  const lastPageRequestRef = useRef(null);
  const previousSortRef = useRef(null);
  const requestedControlledPage = Number.isFinite(Number(controlledPageIndex)) ? Math.floor(Number(controlledPageIndex)) : 0;
  const safeControlledPageCount = Math.max(Math.floor(Number(controlledPageCount)) || 1, 1);
  const safeControlledPageIndex = Math.min(Math.max(requestedControlledPage, 0), safeControlledPageCount - 1);
  const safePageSize = Math.max(Math.floor(Number(initialPageSize)) || 10, 1);

  const tableColumns = useMemo(() => columns.map((column) => {
    const definition = {
      Header: column.header,
      accessor: column.accessor || column.key,
      disableFilters: !column.filter,
      disableGlobalFilter: column.searchable === false,
      disableSortBy: column.sortable === false,
      filter: filterTypes[column.filter?.type] || "text",
      hideable: column.hideable !== false,
      id: column.key,
      sortType: column.sortValue
        ? (rowA, rowB) => String(column.sortValue(rowA.original)).localeCompare(String(column.sortValue(rowB.original)), undefined, { numeric: true })
        : "alphanumeric",
    };
    if (column.render) definition.Cell = ({ row }) => column.render(row.original);
    return definition;
  }), [columns]);

  const initialState = useMemo(() => ({
    filters: Object.entries(initialFilters).filter(([, value]) => value !== undefined && value !== null && value !== "").map(([id, value]) => ({ id, value })),
    hiddenColumns: columns.filter((column) => column.initiallyHidden).map((column) => column.key),
    pageIndex: 0,
    pageSize: safePageSize,
    sortBy: initialSort ? [{ id: initialSort.key, desc: initialSort.direction === "desc" }] : [],
  }), [columns, initialFilters, initialSort, safePageSize]);

  const controlTableState = useCallback((state) => manual ? {
    ...state,
    pageIndex: safeControlledPageIndex,
    pageSize: safePageSize,
  } : state, [manual, safeControlledPageIndex, safePageSize]);

  const table = useTable({
    columns: tableColumns, data, getRowId: (row) => String(getRowId(row)), initialState,
    autoResetFilters: false, autoResetGlobalFilter: false, autoResetHiddenColumns: false,
    autoResetPage: false, autoResetSelectedRows: false,
    manualFilters: manual,
    manualGlobalFilter: manual,
    manualPagination: manual,
    manualSortBy: manual,
    pageCount: manual ? safeControlledPageCount : undefined,
    useControlledState: controlTableState,
  }, useFilters, useGlobalFilter, useSortBy, usePagination, useRowSelect);

  const {
    allColumns, canNextPage, canPreviousPage, getTableBodyProps, getTableProps, gotoPage,
    headerGroups, page, pageCount, prepareRow, rows, selectedFlatRows,
    setAllFilters, setFilter, setGlobalFilter, setPageSize,
    state: { filters, globalFilter, pageIndex, pageSize, sortBy }, toggleAllRowsSelected, visibleColumns,
  } = table;

  const effectivePageCount = Math.max(Number(pageCount) || 1, 1);
  const clampPage = useCallback((index) => Math.min(Math.max(Math.floor(Number(index)) || 0, 0), effectivePageCount - 1), [effectivePageCount]);
  const changePage = useCallback((index) => {
    const targetPage = clampPage(index);
    setGoToPageValue(String(targetPage + 1));
    if (targetPage === pageIndex) return;
    if (manual) {
      if (lastPageRequestRef.current === targetPage) return;
      lastPageRequestRef.current = targetPage;
      onPageChange?.(targetPage);
    } else gotoPage(targetPage);
  }, [clampPage, gotoPage, manual, onPageChange, pageIndex]);

  useEffect(() => { setGoToPageValue(String(clampPage(pageIndex) + 1)); }, [clampPage, pageIndex]);
  useEffect(() => { lastPageRequestRef.current = null; }, [pageIndex]);
  useEffect(() => {
    if (!manual) return;
    if (requestedControlledPage === safeControlledPageIndex) {
      correctedPageRef.current = null;
      return;
    }

    const correctionKey = `${requestedControlledPage}:${safeControlledPageCount}`;
    if (correctedPageRef.current === correctionKey) return;
    correctedPageRef.current = correctionKey;
    onPageChange?.(safeControlledPageIndex);
  }, [manual, onPageChange, requestedControlledPage, safeControlledPageCount, safeControlledPageIndex]);
  useEffect(() => {
    if (!manual && pageIndex >= effectivePageCount) changePage(effectivePageCount - 1);
  }, [changePage, effectivePageCount, manual, pageIndex]);
  useEffect(() => {
    const sortKey = JSON.stringify(sortBy);
    if (previousSortRef.current === null) {
      previousSortRef.current = sortKey;
      return;
    }
    if (previousSortRef.current !== sortKey) {
      previousSortRef.current = sortKey;
      changePage(0);
    }
  }, [changePage, sortBy]);
  useEffect(() => {
    if (!manual || !onQueryChange) return;
    onQueryChange({ filters: Object.fromEntries(filters.map((item) => [item.id, item.value])), search: globalFilter || "", sortBy });
  }, [filters, globalFilter, manual, onQueryChange, sortBy]);
  useEffect(() => {
    const requestedPage = Number(goToPageValue);
    if (!goToPageValue || !Number.isInteger(requestedPage) || requestedPage < 1) return undefined;

    const timeout = setTimeout(() => changePage(requestedPage - 1), 500);

    return () => clearTimeout(timeout);
  }, [changePage, goToPageValue]);

  const displayedRows = manual ? rows : (pagination ? page : rows);
  const selectedRows = selectedFlatRows.map((row) => row.original);
  const activeFilterCount = filters.length + (globalFilter ? 1 : 0);
  const resultCount = manual ? (totalRows === undefined ? rows.length : Math.max(Number(totalRows) || 0, 0)) : rows.length;
  const rangeStart = resultCount ? pageIndex * pageSize + 1 : 0;
  const rangeEnd = manual ? Math.min((pageIndex + 1) * pageSize, resultCount) : (pagination ? Math.min((pageIndex + 1) * pageSize, rows.length) : rows.length);
  const canGoPrevious = manual ? pageIndex > 0 : canPreviousPage;
  const canGoNext = manual ? pageIndex + 1 < effectivePageCount : canNextPage;
  const availablePageSizes = [...new Set([safePageSize, ...pageSizeOptions.map(Number).filter((size) => Number.isInteger(size) && size > 0)])].sort((a, b) => a - b);
  const filterableColumns = columns.filter((column) => column.filter);
  const hideableColumns = allColumns.filter((column) => column.hideable);
  const hasFilterControl = showFilters && filterableColumns.length > 0;
  const hasColumnControl = showColumnSelector && hideableColumns.length > 0;
  const hasExportControl = showExport && Boolean(onExport);
  const hasControls = showSearch || hasFilterControl || hasColumnControl || hasExportControl;
  const hasToolbar = Boolean(title) || showCount || hasControls;

  const clearFilters = () => {
    setAllFilters([]);
    setGlobalFilter(undefined);
    changePage(0);
    onClearFilters?.();
  };

  return (
    <section className="wsm-data-table" aria-busy={loading || undefined} aria-label={caption}>
      {hasToolbar && <div className="wsm-data-table__toolbar">
        {(title || showCount) && <div className="wsm-data-table__heading">{title && <h2>{title}</h2>}{showCount && <span>{resultCount} records</span>}</div>}
        {hasControls && <div className="wsm-data-table__controls">
          {showSearch && <label className="wsm-data-table__search" htmlFor={`${tableId}-search`}>
            <span className="wsm-sr-only">{searchPlaceholder}</span><Search aria-hidden="true" />
            <input id={`${tableId}-search`} onChange={(event) => { setGlobalFilter(event.target.value || undefined); changePage(0); }} placeholder={searchPlaceholder} title={searchPlaceholder} type="search" value={globalFilter || ""} />
          </label>}
          <div className="wsm-data-table__tools">
            {hasFilterControl && <Button aria-controls={`${tableId}-filters`} aria-expanded={activePanel === "filters"} icon={ListFilter} onClick={() => setActivePanel((panel) => panel === "filters" ? null : "filters")} size="compact" variant="ghost">Filters{filters.length > 0 ? ` (${filters.length})` : ""}</Button>}
            {hasColumnControl && <Button aria-controls={`${tableId}-columns`} aria-expanded={activePanel === "columns"} icon={Columns3} onClick={() => setActivePanel((panel) => panel === "columns" ? null : "columns")} size="compact" variant="ghost">Columns</Button>}
            {activeFilterCount > 0 && <Button icon={RotateCcw} onClick={clearFilters} size="compact" variant="ghost">Clear</Button>}
            {hasExportControl && <Button icon={Download} onClick={() => onExport(rows.map((row) => row.original))} size="compact" variant="ghost">Export</Button>}
          </div>
        </div>}
      </div>}

      {activePanel === "filters" && <section className="wsm-data-table__panel" id={`${tableId}-filters`} aria-label="Table filters">
        <div className="wsm-data-table__panel-heading"><strong>Filter records</strong>{filters.length > 0 && <button onClick={clearFilters} type="button">Clear filters</button>}</div>
        <div className="wsm-data-table__filter-grid">{filterableColumns.map((column) => {
          const value = filters.find((item) => item.id === column.key)?.value || "";
          if (column.filter.type === "multiselect") {
            const selected = Array.isArray(value) ? value.map(String) : [];
            return <div className="wsm-data-table__filter-field" key={column.key}>
              <label htmlFor={`${tableId}-filter-${column.key}`}>{column.filter.label || column.header}</label>
              <ReactSelect
                className="wsm-search-select"
                classNamePrefix="wsm-search-select"
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                inputId={`${tableId}-filter-${column.key}`}
                instanceId={`${tableId}-filter-${column.key}`}
                isClearable
                isMulti
                menuPortalTarget={document.body}
                noOptionsMessage={() => "No options found"}
                onChange={(selection) => { setFilter(column.key, selection.length > 0 ? selection.map((option) => option.value) : undefined); changePage(0); }}
                options={column.filter.options}
                placeholder={column.filter.placeholder || "All"}
                unstyled
                value={column.filter.options.filter((option) => selected.includes(String(option.value)))}
              />
            </div>;
          }
          return <label key={column.key}><span>{column.filter.label || column.header}</span>{column.filter.type === "select"
            ? <select onChange={(event) => { setFilter(column.key, event.target.value || undefined); changePage(0); }} value={value}><option value="">All</option>{column.filter.options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
            : <input onChange={(event) => { setFilter(column.key, event.target.value || undefined); changePage(0); }} placeholder={column.filter.placeholder} type={column.filter.type === "date" ? "date" : "text"} value={value} />}</label>;
        })}</div>
      </section>}

      {activePanel === "columns" && <section className="wsm-data-table__panel" id={`${tableId}-columns`} aria-label="Visible columns">
        <div className="wsm-data-table__panel-heading"><strong>Visible columns</strong><span>{visibleColumns.length} shown</span></div>
        <div className="wsm-data-table__column-options">{hideableColumns.map((column) => <label key={column.id}>
          <input checked={column.isVisible} disabled={column.isVisible && visibleColumns.length === 1} onChange={() => column.toggleHidden(column.isVisible)} type="checkbox" /><span>{column.render("Header")}</span>
        </label>)}</div>
      </section>}

      {selectedRows.length > 0 && <div className="wsm-data-table__bulk" role="status"><strong>{selectedRows.length} selected</strong><div>
        {bulkActions.map((action) => <Button key={action.label} onClick={() => action.onClick(selectedRows)} size="compact" variant={action.variant || "outline"}>{action.label}</Button>)}
        <button className="wsm-data-table__clear" onClick={() => toggleAllRowsSelected(false)} type="button">Clear selection</button>
      </div></div>}

      {error ? <div className="wsm-data-table__error" role="alert"><AlertCircle aria-hidden="true" /><div><strong>Unable to load records</strong><p>{error}</p></div>{onRetry && <Button onClick={onRetry} size="compact" variant="outline">Try again</Button>}</div>
        : loading ? <TableSkeleton columns={Math.min(visibleColumns.length + (selectable ? 1 : 0), 6)} label={`Loading ${caption}`} rows={5} />
        : rows.length === 0 ? <EmptyState actions={emptyAction} description={emptyDescription} title={emptyTitle} />
        : <TableResults actions={actions} caption={caption} displayedRows={displayedRows} getTableBodyProps={getTableBodyProps} getTableProps={getTableProps} headerGroups={headerGroups} prepareRow={prepareRow} selectable={selectable} table={table} />}

      {pagination && !error && !loading && rows.length > 0 && <nav className="wsm-data-table__pagination" aria-label={caption + " pagination"}>
        <p aria-live="polite">Showing {rangeStart}-{rangeEnd} of {resultCount}</p>

        <div className="wsm-data-table__page-controls">
          <IconButton aria-label="First page" disabled={!canGoPrevious} icon={ChevronFirst} onClick={() => changePage(0)} size="compact" variant="ghost" />
          <IconButton aria-label="Previous page" disabled={!canGoPrevious} icon={ChevronLeft} onClick={() => changePage(pageIndex - 1)} size="compact" variant="ghost" />
          <span>Page {pageIndex + 1} of {effectivePageCount}</span>
          <IconButton aria-label="Next page" disabled={!canGoNext} icon={ChevronRight} onClick={() => changePage(pageIndex + 1)} size="compact" variant="ghost" />
          <IconButton aria-label="Last page" disabled={!canGoNext} icon={ChevronLast} onClick={() => changePage(effectivePageCount - 1)} size="compact" variant="ghost" />
        </div>

        <div className="wsm-data-table__footer-settings">
          <div className="wsm-data-table__go-to">
            <label htmlFor={tableId + "-page"}>Go to page</label>
            <input id={tableId + "-page"} max={effectivePageCount} min="1" onBlur={() => { const requestedPage = Number(goToPageValue); if (Number.isInteger(requestedPage) && requestedPage >= 1) changePage(requestedPage - 1); else setGoToPageValue(String(pageIndex + 1)); }} onChange={(event) => setGoToPageValue(event.target.value)} step="1" type="number" value={goToPageValue} />
          </div>
          <label className="wsm-data-table__page-size">
            <span className="wsm-sr-only">Rows per page</span>
            <select onChange={(event) => { const size = Number(event.target.value); if (manual) { onPageSizeChange?.(size); changePage(0); } else { setPageSize(size); gotoPage(0); setGoToPageValue("1"); } }} value={pageSize}>
              {availablePageSizes.map((size) => <option key={size} value={size}>{size} rows</option>)}
            </select>
          </label>
        </div>
      </nav>}
    </section>
  );
};

const TableResults = ({ actions, caption, displayedRows, getTableBodyProps, getTableProps, headerGroups, prepareRow, selectable, table }) => <>
  <div className="wsm-data-table__desktop"><table {...getTableProps()}><caption className="wsm-sr-only">{caption}</caption><thead>{headerGroups.map((group) => <tr {...group.getHeaderGroupProps()}>
    {selectable && <th className="wsm-data-table__select" scope="col"><SelectionCheckbox aria-label="Select all rows on this page" {...table.getToggleAllPageRowsSelectedProps()} /></th>}
    {group.headers.map((column) => <th {...column.getHeaderProps()} scope="col">{column.disableSortBy ? column.render("Header") : <button {...column.getSortByToggleProps()} aria-label={`Sort by ${column.render("Header")}`} type="button"><span>{column.render("Header")}</span><SortIndicator column={column} /></button>}</th>)}
    {actions.length > 0 && <th className="wsm-data-table__actions-heading" scope="col"><span className="wsm-sr-only">Actions</span></th>}
  </tr>)}</thead><tbody {...getTableBodyProps()}>{displayedRows.map((row) => { prepareRow(row); return <tr {...row.getRowProps()}>
    {selectable && <td className="wsm-data-table__select"><SelectionCheckbox aria-label={`Select ${row.original.accessibleName || row.id}`} {...row.getToggleRowSelectedProps()} /></td>}
    {row.cells.map((cell) => <td {...cell.getCellProps()}>{cell.render("Cell")}</td>)}{actions.length > 0 && <td className="wsm-data-table__actions"><RowActions actions={actions} row={row.original} /></td>}
  </tr>; })}</tbody></table></div>
  <div className="wsm-data-table__mobile" role="list" aria-label={caption}>{displayedRows.map((row) => { prepareRow(row); return <article className="wsm-data-table__card" key={row.id} role="listitem"><div className={["wsm-data-table__card-header", selectable && "wsm-data-table__card-header--selectable"].filter(Boolean).join(" ")}>
    {selectable && <SelectionCheckbox aria-label={`Select ${row.original.accessibleName || row.id}`} {...row.getToggleRowSelectedProps()} />}<strong>{row.cells[0].render("Cell")}</strong>{actions.length > 0 && <RowActions actions={actions} row={row.original} />}
  </div><dl>{row.cells.slice(1).map((cell) => <div key={cell.column.id}><dt>{cell.column.render("Header")}</dt><dd>{cell.render("Cell")}</dd></div>)}</dl></article>; })}</div>
</>;

const SortIndicator = ({ column }) => <span aria-hidden="true" className={["wsm-data-table__sort", column.isSorted && "is-sorted", column.isSortedDesc && "is-descending"].filter(Boolean).join(" ")} />;
const RowActions = ({ actions, row }) => {
  const [open, setOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const label = "Actions for " + (row.accessibleName || row.id);

  useEffect(() => {
    if (!open) return undefined;

    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;

      const rect = trigger.getBoundingClientRect();
      const menuWidth = menuRef.current?.offsetWidth || 180;
      const menuHeight = menuRef.current?.offsetHeight || actions.length * 36 + 8;
      const spaceBelow = window.innerHeight - rect.bottom;
      const top = spaceBelow >= menuHeight + 8 ? rect.bottom + 4 : rect.top - menuHeight - 4;
      const left = Math.min(Math.max(8, rect.right - menuWidth), window.innerWidth - menuWidth - 8);
      setPosition({ left, top: Math.max(8, top) });
    };

    const closeOnOutsideClick = (event) => {
      if (!triggerRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) setOpen(false);
    };
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };

    updatePosition();
    menuRef.current?.querySelector("button")?.focus();
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [actions.length, open]);

  const handleMenuKeyDown = (event) => {
    const items = [...(menuRef.current?.querySelectorAll("button") || [])];
    const currentIndex = items.indexOf(document.activeElement);
    let nextIndex;

    if (event.key === "ArrowDown") nextIndex = (currentIndex + 1) % items.length;
    if (event.key === "ArrowUp") nextIndex = (currentIndex - 1 + items.length) % items.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = items.length - 1;

    if (nextIndex !== undefined) {
      event.preventDefault();
      items[nextIndex]?.focus();
    }
  };
  return (
    <div className="wsm-row-actions">
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={label}
        className="wsm-row-actions__trigger"
        onClick={() => setOpen((value) => !value)}
        ref={triggerRef}
        title={label}
        type="button"
      >
        <MoreHorizontal aria-hidden="true" />
      </button>
      {open && createPortal(
        <div className="wsm-row-actions__menu" onKeyDown={handleMenuKeyDown} ref={menuRef} role="menu" style={position}>
          {actions.filter((action) => !action.isVisible || action.isVisible(row)).map((action) => (
            <button
              aria-busy={pendingAction === action.label || undefined}
              disabled={Boolean(pendingAction)}
              key={action.label}
              onClick={async () => {
                const result = action.onClick(row);
                if (result && typeof result.then === "function") {
                  setPendingAction(action.label);
                  try {
                    await result;
                  } finally {
                    setPendingAction(null);
                    setOpen(false);
                  }
                  return;
                }
                setOpen(false);
              }}
              role="menuitem"
              type="button"
            >
              {pendingAction === action.label && <LoaderCircle aria-hidden="true" className="wsm-row-actions__spinner" />}
              {pendingAction === action.label ? action.loadingLabel || "Working..." : action.label}
            </button>
          ))}
        </div>,
        document.body,
      )}
    </div>
  );
};
const actionType = PropTypes.shape({ isVisible: PropTypes.func, label: PropTypes.string.isRequired, loadingLabel: PropTypes.string, onClick: PropTypes.func.isRequired, variant: PropTypes.string });
const optionType = PropTypes.shape({ label: PropTypes.string.isRequired, value: PropTypes.string.isRequired });
const filterType = PropTypes.shape({ label: PropTypes.string, options: PropTypes.arrayOf(optionType), placeholder: PropTypes.string, type: PropTypes.oneOf(["date", "multiselect", "select", "text"]).isRequired });
ResponsiveDataTable.propTypes = { actions: PropTypes.arrayOf(actionType), bulkActions: PropTypes.arrayOf(actionType), caption: PropTypes.string.isRequired, columns: PropTypes.arrayOf(PropTypes.shape({ accessor: PropTypes.func, filter: filterType, header: PropTypes.string.isRequired, hideable: PropTypes.bool, initiallyHidden: PropTypes.bool, key: PropTypes.string.isRequired, render: PropTypes.func, searchable: PropTypes.bool, sortable: PropTypes.bool, sortValue: PropTypes.func })).isRequired, data: PropTypes.arrayOf(PropTypes.object).isRequired, emptyAction: PropTypes.node, emptyDescription: PropTypes.string, emptyTitle: PropTypes.string, error: PropTypes.string, getRowId: PropTypes.func, initialFilters: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool, PropTypes.arrayOf(PropTypes.string)])), initialSort: PropTypes.shape({ direction: PropTypes.oneOf(["asc", "desc"]).isRequired, key: PropTypes.string.isRequired }), loading: PropTypes.bool, manual: PropTypes.bool, onClearFilters: PropTypes.func, onExport: PropTypes.func, onPageChange: PropTypes.func, onPageSizeChange: PropTypes.func, onQueryChange: PropTypes.func, onRetry: PropTypes.func, pageCount: PropTypes.number, pageIndex: PropTypes.number, pagination: PropTypes.bool, pageSize: PropTypes.number, pageSizeOptions: PropTypes.arrayOf(PropTypes.number), searchPlaceholder: PropTypes.string, selectable: PropTypes.bool, showColumnSelector: PropTypes.bool, showCount: PropTypes.bool, showExport: PropTypes.bool, showFilters: PropTypes.bool, showSearch: PropTypes.bool, title: PropTypes.string, totalRows: PropTypes.number };
SelectionCheckbox.propTypes = { indeterminate: PropTypes.bool };
SortIndicator.propTypes = { column: PropTypes.shape({ isSorted: PropTypes.bool, isSortedDesc: PropTypes.bool }).isRequired };
RowActions.propTypes = { actions: PropTypes.arrayOf(actionType).isRequired, row: PropTypes.object.isRequired };
TableResults.propTypes = { actions: PropTypes.arrayOf(actionType).isRequired, caption: PropTypes.string.isRequired, displayedRows: PropTypes.arrayOf(PropTypes.object).isRequired, getTableBodyProps: PropTypes.func.isRequired, getTableProps: PropTypes.func.isRequired, headerGroups: PropTypes.arrayOf(PropTypes.object).isRequired, prepareRow: PropTypes.func.isRequired, selectable: PropTypes.bool.isRequired, table: PropTypes.object.isRequired };

export default ResponsiveDataTable;
